import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { useQuery } from '@apollo/client';
import { Button, LoadingSmall, AlertService, Select, SelectOption, Theme as theme } from '@spoiler-alert/ui-library';
import { fetchWithToken } from '../../services/fetch-with-token';
import { AllSellerSiteNamesAndIdsQuery } from './exclusion-queries';

const useStyles = createUseStyles({
  siteSelectContainer: {
    display: 'flex',
    gap: 8,
    marginBottom: 8,
  },
  siteSelect: {
    minWidth: 220,
  },
  fileInputContainer: {
    marginBottom: 12,
  },
  buttonContainer: {
    display: 'flex',
    gap: 8,
  },
  lastImportInfo: {
    marginTop: 12,
    padding: 8,
    background: theme.infoBackgroundColor,
    borderRadius: 4,
    fontSize: 14,
  },
  importRunId: {
    fontFamily: 'monospace',
    userSelect: 'all',
  },
  bold: {
    fontWeight: 'bold',
  },
});

const ReconciliationUploadWidget = ({ uploadUrl }) => {
  const classes = useStyles();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [lastBulkImportRunId, setLastBulkImportRunId] = useState(null);

  const { loading: sitesLoading, data: sitesData } = useQuery(AllSellerSiteNamesAndIdsQuery);

  const sites = useMemo(() => {
    return sitesData?.allSellerSiteNamesAndIdsQuery.map((s) => ({ value: s._id, text: s.siteName })) || [];
  }, [sitesData]);

  const onFileChange = (e) => setFile(e.target.files && e.target.files[0]);

  const upload = async () => {
    if (!file) return AlertService.alert({ type: 'warning', message: 'Please choose a file to upload.' });
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('site', selectedSite.value);
      const resp = await fetchWithToken(uploadUrl, { method: 'POST', body: form });
      if (resp.status === 200) {
        const data = await resp.json();
        setLastBulkImportRunId(data.bulkImportRunId);
        AlertService.alert({
          type: 'success',
          message: 'Reconciliation file uploaded — import queued.',
          autoDismiss: true,
        });
      } else {
        AlertService.alert({ type: 'warning', message: 'Upload failed. Please check the file and try again.' });
      }
    } catch {
      AlertService.alert({ type: 'warning', message: 'Upload failed. Please try again.' });
    }
    setUploading(false);
  };

  return (
    <div>
      <h4>Reconciliation Upload</h4>
      <div className={classes.fileInputContainer}>
        <div className={classes.siteSelectContainer}>
          <div className={classes.siteSelect}>
            <Select
              minimal
              placeholderText={sitesLoading ? 'Loading Seller Sites...' : 'Select a Seller Site'}
              onChange={(e) => setSelectedSite(e[0])}
              selectedItem={selectedSite}
              search
              multiple={false}
            >
              {sites.map((s) => (
                <SelectOption key={s.value} value={s.value}>
                  {s.text}
                </SelectOption>
              ))}
            </Select>
          </div>
        </div>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={onFileChange} />
      </div>
      <div className={classes.buttonContainer}>
        <Button onClick={upload} disabled={uploading || !selectedSite}>
          {uploading ? <LoadingSmall /> : 'Upload & Queue'}
        </Button>
      </div>
      {lastBulkImportRunId && (
        <div className={classes.lastImportInfo}>
          <span className={classes.bold}>Last Import Run ID:</span>{' '}
          <span className={classes.importRunId}>{lastBulkImportRunId}</span>
        </div>
      )}
    </div>
  );
};

ReconciliationUploadWidget.propTypes = {
  uploadUrl: PropTypes.string.isRequired,
};

export default ReconciliationUploadWidget;
